/**
 * StockMoveDialog - Fluxo premium para movimentar um ou vários itens.
 *
 * Cada item vira uma linha própria no histórico (sem quantidade). Só lista
 * kits montados e rastreadores/chips fora de kit (BR-EST-001). Destino:
 * endereço, prestador ou veículo do cliente (BR-EST-002). A validação
 * completa e atômica permanece em `move_stock_items_atomic`.
 *
 * RELEASE: 2026-10-10 v2.9.0 - Item selecionado passa a usar círculo verde com marca de confirmação branca.
 * RELEASE: 2026-10-10 v2.8.0 - Visual premium: cabeçalho com selo, etapas com progresso,
 *          linhas com destaque/elevação, esqueletos de carregamento, animações e rodapé translúcido.
 * RELEASE: 2026-10-10 v2.6.1 - Resumo lateral exibe o local atual de cada item selecionado.
 * RELEASE: 2026-10-10 v2.6.0 - Atualiza itens ao abrir, por Realtime/polling e revalida
 *          a seleção antes de confirmar para evitar movimentação com local antigo.
 * RELEASE: 2026-10-10 v2.5.0 - Kit vinculado a cliente/veículo só permite destino de estoque.
 * RELEASE: 2026-10-10 v2.4.0 - Organiza a aba Kits em colunas no desktop e cartões no celular.
 * RELEASE: 2026-10-10 v2.3.1 - Impede avançar de Destino sem itens selecionados.
 * RELEASE: 2026-10-10 v2.3.0 - Destinos respeitam situação e origem; kit adota hierarquia
 *          visual código, IMEI, modelo, ICCID e local atual.
 * RELEASE: 2026-10-10 v2.2.0 - Exibe modelo do rastreador em kits/rastreadores e detalhes
 *          do plano exclusivamente na lista de chips.
 * RELEASE: 2026-10-10 v2.1.0 - Modal maior; kits mostram IMEI/ICCID e situações na lista,
 *          resumo e revisão; kit indisponível bloqueado; Cliente só aceita kits.
 * RELEASE: 2026-10-10 v2.0.0 - Organiza a movimentação em Itens, Destino e
 *          Revisão, com resumo persistente e experiência responsiva.
 * RELEASE: 2026-10-10 v1.1.0 - Corrige o direcionamento transversal para Prestadores.
 * RELEASE: 2026-10-06 v1.0.0 - Versão inicial (Fase 4 do Estoque).
 */
import { Skeleton } from '@/components/ui/skeleton';
import { useEffect, useMemo, useState, type ComponentType } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Box,
  Car,
  Check,
  CheckCircle2,
  ChevronRight,
  CreditCard,
  Cpu,
  MapPin,
  PackageCheck,
  MapPinned,
  Loader2,
  X,
  Search,
  Trash2,
  Wrench,
  Truck,
} from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { InlineNotice } from '@/components/ui/inline-notice';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import type { StockItemKind } from '@/hooks/useStockItems';
import {
  STOCK_DESTINATION_TYPES,
  useMoveStockItems,
  useRevalidateStockMovableItems,
  useStockDestinations,
  useStockMovableItems,
  useStockMovableItemsRealtime,
  type MovableItem,
  type StockDestinationType,
} from '@/hooks/useStockMovements';
import { StockPlaceLabel } from './StockPlaceLabel';
import { canAdvanceStockMovement, canMoveSelectionToCustomer, canMoveSelectionToProvider, hasCustomerLinkedKit, stockStatusLabel } from '@/lib/stock-presentation';

/** Etiqueta compacta da situação; verde quando Ativo, âmbar nos demais casos. */
function StatusPill({ kind, value }: { kind: StockItemKind; value?: string | null }) {
  if (!value) return null;
  const ok = value === 'active';
  return (
    <span className={cn('inline-flex h-4 items-center rounded px-1.5 text-[9px] font-semibold', ok ? 'bg-success/15 text-success' : 'bg-warning/20 text-warning')}>
      {stockStatusLabel(kind, value)}
    </span>
  );
}

/**
 * Indicador de seleção do item na coluna "Ação".
 * Selecionado: círculo verde com marca de confirmação branca.
 * Não selecionado: pílula neutra "Selecionar" (mantém a orientação de clique).
 */
function SelectionIndicator({ selected }: { selected: boolean }) {
  if (selected) {
    return (
      <span
        role="img"
        aria-label="Selecionado"
        title="Selecionado"
        className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-success text-success-foreground shadow-sm ring-2 ring-success/25 transition-transform duration-200 group-hover:scale-110"
      >
        <Check className="h-3.5 w-3.5" strokeWidth={3} />
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2.5 py-1 text-[11px] font-semibold text-muted-foreground transition-colors group-hover:border-primary/40 group-hover:text-primary">
      Selecionar
    </span>
  );
}

/** Formata o pacote do plano sem perder precisão nos pacotes menores. */
function formatDataPackage(megabytes?: number | null) {
  if (megabytes === null || megabytes === undefined) return '—';
  if (megabytes < 1024) return `${megabytes} MB`;
  const gigabytes = megabytes / 1024;
  return `${Number.isInteger(gigabytes) ? gigabytes : gigabytes.toFixed(1)} GB`;
}

/** Modelo associado ao rastreador, compartilhado pela lista e pelo resumo. */
function TrackerModel({ item }: { item: MovableItem }) {
  if (item.type === 'chip') return null;
  return (
    <span className="mt-0.5 block truncate text-[10px] font-normal text-muted-foreground">
      Modelo: {item.tracker_model_name ?? 'Não informado'}
    </span>
  );
}

/** Linhas do rastreador e do chip vinculados ao kit, com situação de cada um. */
function KitComponents({ item }: { item: MovableItem }) {
  if (item.type !== 'kit') return null;
  return (
    <span className="mt-1.5 grid gap-1 text-[11px] font-normal text-muted-foreground">
      <span className="flex min-w-0 items-center gap-1.5">
        <Cpu className="h-3 w-3 shrink-0" />
        <span className="truncate tabular-nums">IMEI {item.tracker_imei ?? item.extra ?? '—'}</span>
        <StatusPill kind="tracker" value={item.tracker_status} />
      </span>
      <span className="pl-[18px]"><TrackerModel item={item} /></span>
      <span className="flex min-w-0 items-center gap-1.5">
        <CreditCard className="h-3 w-3 shrink-0" />
        <span className="truncate tabular-nums">ICCID {item.chip_iccid ?? '—'}</span>
        <StatusPill kind="chip" value={item.chip_status} />
      </span>
    </span>
  );
}

/** Cabeçalho tabular exclusivo da aba Kits em telas com espaço para comparação. */
function KitListHeader() {
  return (
    <div className="sticky top-0 z-10 hidden min-w-[760px] grid-cols-[64px_minmax(120px,0.8fr)_88px_minmax(230px,1.6fr)_minmax(130px,0.9fr)_80px] items-center gap-3 border-b bg-muted/70 px-3 py-2.5 text-[10px] font-semibold uppercase text-muted-foreground md:grid">
      <span>Tipo</span>
      <span>Identificador</span>
      <span>Situação</span>
      <span>Detalhes</span>
      <span>Local atual</span>
      <span className="text-right">Ação</span>
    </div>
  );
}

/** Linha responsiva de kit: colunas comparáveis no desktop e cartão compacto no celular. */
function KitCandidateRow({ item, selected, onToggle }: { item: MovableItem; selected: boolean; onToggle: () => void }) {
  return (
    <Button
      type="button"
      variant="ghost"
      onClick={onToggle}
      aria-pressed={selected}
      className={cn(
        'group relative h-auto w-full min-w-0 rounded-none border-b border-l-2 border-l-transparent px-3 py-3 text-left transition-all duration-200 last:border-b-0 hover:bg-muted/50 hover:shadow-sm',
        'grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-3 gap-y-2',
        'md:min-w-[760px] md:grid-cols-[64px_minmax(120px,0.8fr)_88px_minmax(230px,1.6fr)_minmax(130px,0.9fr)_80px] md:items-center',
        selected && 'border-l-primary bg-primary/[0.07] hover:bg-primary/10',
      )}
    >
      <span className="flex h-8 items-center md:h-auto">
        <Badge variant="outline" className="h-6 px-2">Kit</Badge>
      </span>

      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold tabular-nums text-foreground">{item.label}</span>
        <span className="mt-0.5 block text-[10px] text-muted-foreground md:hidden">Identificador</span>
      </span>

      <span className="flex min-h-8 items-center md:min-h-0">
        <StatusPill kind="kit" value={item.status} />
      </span>

      <span className="col-span-3 min-w-0 border-t pt-2 md:col-span-1 md:border-0 md:pt-0">
        <span className="grid gap-1 text-[11px] font-normal text-muted-foreground">
          <span className="flex min-w-0 items-center gap-1.5">
            <Cpu className="h-3 w-3 shrink-0" />
            <span className="truncate tabular-nums"><span className="font-medium text-foreground">IMEI:</span> {item.tracker_imei ?? item.extra ?? '—'}</span>
            <StatusPill kind="tracker" value={item.tracker_status} />
          </span>
          <span className="flex min-w-0 items-center gap-1.5">
            <CreditCard className="h-3 w-3 shrink-0" />
            <span className="truncate tabular-nums"><span className="font-medium text-foreground">ICCID:</span> {item.chip_iccid ?? '—'}</span>
            <StatusPill kind="chip" value={item.chip_status} />
          </span>
          <span className="truncate pl-[18px]"><span className="font-medium text-foreground">Modelo:</span> {item.tracker_model_name ?? 'Não informado'}</span>
          {item.eligible === false && (
            <span className="text-[11px] font-medium text-destructive">Componentes não ativos: somente retorno ao estoque</span>
          )}
        </span>
      </span>

      <span className="col-span-2 min-w-0 md:col-span-1">
        <StockPlaceLabel place={item.place} compact />
      </span>

      {/* Ação com estado claro: círculo verde de confirmação quando o item está selecionado */}
      <span className="justify-self-end">
        <SelectionIndicator selected={selected} />
      </span>
    </Button>
  );
}

/** Informações comerciais do chip, exibidas somente na aba Chips para evitar poluição. */
function ChipPlanDetails({ item }: { item: MovableItem }) {
  if (item.type !== 'chip') return null;
  const carriers = item.chip_carriers?.filter(Boolean).join(', ') || 'Não informada';
  return (
    <span className="mt-1 grid gap-0.5 text-[11px] font-normal text-muted-foreground">
      <span className="truncate">Plano: {item.chip_plan_name ?? 'Não informado'} · {formatDataPackage(item.chip_data_package_mb)}</span>
      <span className="truncate">Fornecedor: {item.chip_supplier_name ?? 'Não informado'}</span>
      <span className="truncate">Operadora(s): {carriers}</span>
    </span>
  );
}

const KIND_CONFIG: Record<StockItemKind, { label: string; singular: string; icon: ComponentType<{ className?: string }> }> = {
  kit: { label: 'Kits', singular: 'Kit', icon: Box },
  tracker: { label: 'Rastreadores', singular: 'Rastreador', icon: Cpu },
  chip: { label: 'Chips', singular: 'Chip', icon: CreditCard },
};

const DESTINATION_ICON: Record<StockDestinationType, ComponentType<{ className?: string }>> = {
  location: MapPin,
  provider: Wrench,
  customer: Car,
  // Logística: destino exclusivo das remessas (não listado na movimentação manual)
  carrier: Truck,
};

const STEPS = [
  { value: 1, label: 'Itens', description: 'Selecione o que será movimentado' },
  { value: 2, label: 'Destino', description: 'Defina para onde os itens irão' },
  { value: 3, label: 'Revisão', description: 'Confira antes de confirmar' },
] as const;

const MAX_ITEMS = 500;

/** Campos operacionais cuja mudança exige nova conferência antes da gravação. */
export function stockMovableOperationalSignature(item: MovableItem) {
  return JSON.stringify({
    place: item.place,
    status: item.status,
    trackerStatus: item.tracker_status,
    chipStatus: item.chip_status,
    eligible: item.eligible,
  });
}

/** Busca com atraso curto para evitar consultas a cada tecla. */
function useDebounced(value: string) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value.trim()), 300);
    return () => clearTimeout(timer);
  }, [value]);
  return debouncedValue;
}

/** Navegação superior que também comunica o progresso do fluxo. */
function MovementSteps({ current }: { current: number }) {
  return (
    <div className="relative shrink-0 border-b bg-muted/20">
    <ol aria-label="Etapas da movimentação" className="grid grid-cols-3 px-4 sm:px-6">
      {STEPS.map((step, index) => {
        const completed = current > step.value;
        const active = current === step.value;
        return (
          <li key={step.value} aria-current={active ? 'step' : undefined} className="relative flex min-w-0 items-center gap-2 py-3 sm:gap-3">
            <span className={cn(
              'relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-semibold transition-all duration-300',
              completed && 'border-success bg-success text-success-foreground',
              active && 'border-primary bg-primary text-primary-foreground shadow-md ring-4 ring-primary/15',
              !active && !completed && 'border-border bg-background text-muted-foreground',
            )}>
              {completed ? <Check className="h-3.5 w-3.5" /> : step.value}
            </span>
            <span className="min-w-0">
              <span className={cn('block truncate text-xs font-semibold sm:text-sm', active ? 'text-foreground' : 'text-muted-foreground')}>{step.label}</span>
              <span className="hidden truncate text-[11px] text-muted-foreground lg:block">{step.description}</span>
            </span>
            {index < STEPS.length - 1 && <ChevronRight className="ml-auto mr-2 hidden h-4 w-4 shrink-0 text-muted-foreground sm:block" />}
          </li>
        );
      })}
    </ol>
    {/* Barra de progresso que se preenche conforme a etapa */}
    <div className="absolute inset-x-0 bottom-0 h-0.5 bg-border/60">
      <div className="h-full bg-gradient-to-r from-primary to-primary/60 transition-all duration-500 ease-out" style={{ width: `${(current / STEPS.length) * 100}%` }} />
    </div>
    </div>
  );
}

/** Painel único de resumo, usado no desktop durante todo o fluxo. */
function MovementSummary({
  selected,
  destination,
  destinationType,
  notes,
  onRemove,
}: {
  selected: MovableItem[];
  destination: { id: string; label: string; extra?: string | null } | null;
  destinationType: StockDestinationType;
  notes: string;
  onRemove: (item: MovableItem) => void;
}) {
  const DestinationIcon = DESTINATION_ICON[destinationType];
  return (
    <aside className="hidden min-h-0 flex-col border-l bg-gradient-to-b from-muted/30 to-muted/10 lg:flex lg:w-80 xl:w-96">
      <div className="border-b px-4 py-3">
        <p className="text-sm font-semibold">Resumo da movimentação</p>
        <p className="text-xs text-muted-foreground">Confira suas escolhas a qualquer momento.</p>
      </div>
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4">
        <section>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase text-muted-foreground">Itens</p>
            <Badge className="tabular-nums shadow-sm">{selected.length}</Badge>
          </div>
          {selected.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed bg-background/60 px-4 py-6 text-center">
              <Box className="h-6 w-6 text-muted-foreground/60" />
              <p className="text-xs text-muted-foreground">Nenhum item selecionado.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {selected.map((item) => {
                const ItemIcon = KIND_CONFIG[item.type].icon;
                return (
                  <div key={`${item.type}-${item.id}`} className="group flex items-start gap-2 rounded-lg border bg-background px-2.5 py-2.5 shadow-sm transition-all duration-200 animate-in fade-in-0 slide-in-from-right-2 hover:border-primary/30 hover:shadow-md">
                    <ItemIcon className="h-4 w-4 shrink-0 text-primary" />
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-1.5 truncate text-xs font-medium tabular-nums">{item.label} <StatusPill kind={item.type} value={item.status} /></p>
                      <p className="truncate text-[10px] text-muted-foreground">{KIND_CONFIG[item.type].singular}</p>
                       {item.type === 'kit' ? <KitComponents item={item} /> : <TrackerModel item={item} />}
                       {/* Localização atual do item: ajuda na análise antes de confirmar a movimentação */}
                       <p className="mt-0.5 flex items-center gap-1 text-[10px] text-muted-foreground">
                         <span className="shrink-0">Local atual:</span>
                         <StockPlaceLabel place={item.place} compact />
                       </p>
                    </div>
                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-muted-foreground opacity-60 transition-opacity hover:text-destructive group-hover:opacity-100" onClick={() => onRemove(item)} aria-label={`Remover ${item.label}`} title="Remover item">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section>
          <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Destino</p>
          {destination ? (
            <div className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/[0.05] p-3 shadow-sm animate-in fade-in-0 zoom-in-95">
              <DestinationIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div className="min-w-0">
                <p className="break-words text-xs font-medium">{destination.label}</p>
                {destination.extra && <p className="mt-0.5 break-words text-[10px] text-muted-foreground">{destination.extra}</p>}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed bg-background/60 px-4 py-6 text-center">
              <MapPinned className="h-6 w-6 text-muted-foreground/60" />
              <p className="text-xs text-muted-foreground">Destino ainda não selecionado.</p>
            </div>
          )}
        </section>

        {notes.trim() && (
          <section>
            <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Observações</p>
            <p className="whitespace-pre-wrap break-words text-xs text-muted-foreground">{notes.trim()}</p>
          </section>
        )}
      </div>
    </aside>
  );
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Itens já escolhidos ao abrir (ex.: ação Movimentar da lista). */
  initialItems?: MovableItem[];
}

export function StockMoveDialog({ open, onOpenChange, initialItems }: Props) {
  const [step, setStep] = useState(1);
  const [kind, setKind] = useState<StockItemKind>('kit');
  const [itemSearch, setItemSearch] = useState('');
  const [selected, setSelected] = useState<MovableItem[]>([]);
  const [destType, setDestType] = useState<StockDestinationType>('location');
  const [destSearch, setDestSearch] = useState('');
  const [dest, setDest] = useState<{ id: string; label: string; extra?: string | null } | null>(null);
  const [notes, setNotes] = useState('');
  const [isRevalidating, setIsRevalidating] = useState(false);
  const move = useMoveStockItems();
  const revalidateItems = useRevalidateStockMovableItems();
  useStockMovableItemsRealtime(open);

  const itemQuery = useDebounced(itemSearch);
  const destinationQuery = useDebounced(destSearch);
  const { data: candidates = [], isLoading: loadingItems } = useStockMovableItems(kind, itemQuery, open && step === 1);
  const { data: destinations = [], isLoading: loadingDestinations } = useStockDestinations(destType, destinationQuery, open && step === 2);
  const selectedByKind = useMemo(() => ({
    kit: selected.filter((item) => item.type === 'kit').length,
    tracker: selected.filter((item) => item.type === 'tracker').length,
    chip: selected.filter((item) => item.type === 'chip').length,
  }), [selected]);

  // O estado é reiniciado em cada abertura para não reaproveitar uma operação anterior.
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setSelected(initialItems ?? []);
    setKind(initialItems?.[0]?.type ?? 'kit');
    setItemSearch('');
    setDestType('location');
    setDestSearch('');
    setDest(null);
    setNotes('');
    setIsRevalidating(false);
  }, [open, initialItems]);

  // Atualiza também o resumo/seleção quando a consulta ativa recebe uma versão mais recente.
  useEffect(() => {
    if (!open || candidates.length === 0) return;
    const latestById = new Map(candidates.map((candidate) => [`${candidate.type}:${candidate.id}`, candidate]));
    setSelected((current) => current.map((item) => latestById.get(`${item.type}:${item.id}`) ?? item));
  }, [candidates, open]);

  // Prestador e cliente recebem somente itens ativos; kit em cliente/veículo só retorna ao estoque.
  const customerLinkedKit = hasCustomerLinkedKit(selected);
  const providerAllowed = canMoveSelectionToProvider(selected);
  const customerAllowed = canMoveSelectionToCustomer(selected);
  useEffect(() => {
    const externalBlocked = (destType === 'provider' && !providerAllowed) || (destType === 'customer' && !customerAllowed);
    if (externalBlocked) { setDestType('location'); setDest(null); }
  }, [customerAllowed, destType, providerAllowed]);

  const isSelected = (item: MovableItem) => selected.some((current) => current.type === item.type && current.id === item.id);

  /** Alterna a seleção sem ultrapassar o limite seguro já adotado pelo fluxo. */
  const toggleItem = (item: MovableItem) => {
    setSelected((current) => {
      if (current.some((selectedItem) => selectedItem.type === item.type && selectedItem.id === item.id)) {
        return current.filter((selectedItem) => !(selectedItem.type === item.type && selectedItem.id === item.id));
      }
      return current.length >= MAX_ITEMS ? current : [...current, item];
    });
  };

  /** Confirma usando exatamente a mesma operação atômica já existente. */
  const submit = async () => {
    if (!dest || selected.length === 0 || move.isPending || isRevalidating) return;
    setIsRevalidating(true);
    try {
      const refreshed = await revalidateItems(selected);
      const available = refreshed.filter((item): item is MovableItem => item !== null);
      const changed = refreshed.some((item, index) => {
        const previous = selected[index];
        return item === null || !previous || stockMovableOperationalSignature(item) !== stockMovableOperationalSignature(previous);
      });

      if (changed) {
        setSelected(available);
        setDest(null);
        setStep(1);
        toast.warning('Os dados dos itens mudaram. Revise a seleção e o local atual antes de continuar.');
        return;
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Não foi possível atualizar os itens selecionados');
      return;
    } finally {
      setIsRevalidating(false);
    }
    move.mutate(
      {
        items: selected.map((item) => ({ type: item.type, id: item.id })),
        destination: { type: destType, id: dest.id },
        notes,
      },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  const canAdvance = canAdvanceStockMovement(step, selected.length, Boolean(dest));
  const DestinationIcon = DESTINATION_ICON[destType];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[92dvh] max-h-[92dvh] flex-col gap-0 overflow-hidden p-0 max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:max-w-full max-sm:rounded-none sm:max-w-[min(92vw,1440px)]">
        <DialogHeader className="relative shrink-0 border-b px-5 py-4 pr-12 sm:px-6">
          <DialogTitle className="flex items-center gap-3 text-left">
            {/* Selo do ícone com fundo suave na cor principal */}
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 text-primary ring-1 ring-primary/20">
              <PackageCheck className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-lg font-semibold tracking-tight">Movimentar itens</span>
              <span className="block text-xs font-normal text-muted-foreground">Transfira kits, rastreadores e chips com segurança</span>
            </span>
          </DialogTitle>
        </DialogHeader>

        <MovementSteps current={step} />

        <div className="flex min-h-0 flex-1">
          <main className="min-w-0 flex-1 overflow-y-auto">
            {step === 1 && (
              <div className="mx-auto flex min-h-full max-w-5xl flex-col gap-4 p-4 duration-300 animate-in fade-in-0 slide-in-from-bottom-2 sm:p-6">
                <div>
                  <h3 className="text-base font-semibold">Selecione os itens</h3>
                  <p className="text-sm text-muted-foreground">Você pode combinar kits, rastreadores e chips na mesma movimentação.</p>
                </div>

                <Tabs value={kind} onValueChange={(value) => setKind(value as StockItemKind)}>
                  <TabsList className="grid h-auto w-full grid-cols-3 rounded-xl bg-muted/70 p-1">
                    {(Object.keys(KIND_CONFIG) as StockItemKind[]).map((itemKind) => {
                      const config = KIND_CONFIG[itemKind];
                      const Icon = config.icon;
                      return (
                        <TabsTrigger key={itemKind} value={itemKind} className="min-w-0 gap-1.5 rounded-lg px-2 py-2 text-xs transition-all data-[state=active]:shadow-md sm:text-sm">
                          <Icon className="h-4 w-4 shrink-0" />
                          <span className="truncate">{config.label}</span>
                          {selectedByKind[itemKind] > 0 && <Badge className="ml-1 h-5 min-w-5 px-1.5 tabular-nums">{selectedByKind[itemKind]}</Badge>}
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>
                </Tabs>

                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input value={itemSearch} maxLength={60} className="h-11 rounded-lg pl-9 pr-9 shadow-sm transition-shadow focus-visible:shadow-md" placeholder="Buscar código, IMEI ou ICCID..." onChange={(event) => setItemSearch(event.target.value)} />
                  {itemSearch && (
                    <button type="button" onClick={() => setItemSearch('')} aria-label="Limpar busca" className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>

                <div className="min-h-60 flex-1 overflow-auto rounded-xl border bg-background shadow-sm">
                  {loadingItems ? (
                    <div aria-label="Carregando itens" className="space-y-0">
                      {/* Linhas-esqueleto animadas durante o carregamento */}
                      {Array.from({ length: 5 }).map((_, index) => (
                        <div key={index} className="flex items-center gap-3 border-b px-3 py-4 last:border-0">
                          <Skeleton className="h-8 w-8 rounded-md" />
                          <div className="flex-1 space-y-2"><Skeleton className="h-3 w-1/3" /><Skeleton className="h-3 w-2/3" /></div>
                          <Skeleton className="h-6 w-20 rounded-full" />
                        </div>
                      ))}
                    </div>
                  ) : candidates.length === 0 ? (
                    <div className="flex h-full min-h-60 flex-col items-center justify-center gap-2 px-6 text-center">
                      <Box className="h-8 w-8 text-muted-foreground" />
                      <p className="text-sm font-medium">Nenhum item disponível</p>
                      <p className="max-w-sm text-xs text-muted-foreground">Itens que fazem parte de um kit são movimentados junto com ele.</p>
                    </div>
                  ) : <>
                    {kind === 'kit' && <KitListHeader />}
                    {candidates.map((candidate) => {
                    const candidateSelected = isSelected(candidate);
                    const ItemIcon = KIND_CONFIG[candidate.type].icon;
                     if (candidate.type === 'kit') {
                       return (
                         <KitCandidateRow
                           key={`${candidate.type}-${candidate.id}`}
                           item={candidate}
                           selected={candidateSelected}
                           onToggle={() => toggleItem(candidate)}
                         />
                       );
                     }
                    return (
                      <Button
                        key={`${candidate.type}-${candidate.id}`}
                        type="button"
                        variant="ghost"
                        onClick={() => toggleItem(candidate)}
                        aria-pressed={candidateSelected}
                        className={cn(
                          'group h-auto w-full justify-start rounded-none border-b border-l-2 border-l-transparent px-3 py-3 text-left transition-all duration-200 last:border-b-0 hover:bg-muted/50 hover:shadow-sm',
                          candidateSelected && 'border-l-primary bg-primary/[0.07] hover:bg-primary/10',
                        )}
                      >
                        <span className={cn(
                          'mr-3 flex h-9 w-9 shrink-0 items-center justify-center rounded-md border bg-muted/40',
                          candidateSelected && 'border-primary bg-primary text-primary-foreground',
                        )}>
                          {candidateSelected ? <Check className="h-4 w-4" /> : <ItemIcon className="h-4 w-4 text-muted-foreground" />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2 truncate text-sm font-semibold tabular-nums text-foreground">
                            {candidate.label}
                             {candidate.extra && <span className="font-normal text-muted-foreground">{candidate.extra}</span>}
                            <StatusPill kind={candidate.type} value={candidate.status} />
                          </span>
                           {candidate.type === 'tracker' && <TrackerModel item={candidate} />}
                           {candidate.type === 'chip' && <ChipPlanDetails item={candidate} />}
                          <StockPlaceLabel place={candidate.place} compact />
                        </span>
                        <span className="ml-2 hidden sm:inline-flex">
                          <SelectionIndicator selected={candidateSelected} />
                        </span>
                      </Button>
                    );
                  })}
                  </>}
                </div>

                <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                  <span><strong className="text-foreground tabular-nums">{selected.length}</strong> de {MAX_ITEMS} itens selecionados</span>
                  {selected.length > 0 && (
                    <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setSelected([])}>Limpar seleção</Button>
                  )}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="mx-auto flex min-h-full max-w-5xl flex-col gap-4 p-4 duration-300 animate-in fade-in-0 slide-in-from-bottom-2 sm:p-6">
                <div>
                  <h3 className="text-base font-semibold">Escolha o destino</h3>
                  <p className="text-sm text-muted-foreground">Defina o novo local dos {selected.length} itens selecionados.</p>
                </div>

                <Tabs value={destType} onValueChange={(value) => {
                  setDestType(value as StockDestinationType);
                  setDest(null);
                  setDestSearch('');
                }}>
                  <TabsList className="grid h-auto w-full grid-cols-3 bg-muted/70 p-1">
                    {STOCK_DESTINATION_TYPES.map((destinationType) => {
                      const Icon = DESTINATION_ICON[destinationType.value];
                      return (
                        <TabsTrigger
                          key={destinationType.value}
                          value={destinationType.value}
                          disabled={(destinationType.value === 'provider' && !providerAllowed) || (destinationType.value === 'customer' && !customerAllowed)}
                          className="min-w-0 gap-1.5 px-2 py-2 text-xs sm:text-sm"
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                          <span className="truncate">{destinationType.label}</span>
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>
                </Tabs>

                {customerLinkedKit ? (
                  <InlineNotice variant="info">
                    Kit vinculado a cliente/veículo só pode ser movimentado manualmente para um endereço de estoque. Use a Ordem de Serviço para outras transferências.
                  </InlineNotice>
                ) : !providerAllowed ? (
                  <InlineNotice variant="info">Itens ou componentes com situação diferente de Ativo só podem ser enviados para um endereço de estoque.</InlineNotice>
                ) : providerAllowed && !customerAllowed ? (
                  <InlineNotice variant="info">
                    Para cliente, use somente kit completo e ativo. Kits já vinculados a cliente/veículo devem ser transferidos pela Ordem de Serviço.
                  </InlineNotice>
                ) : null}

                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={destSearch}
                    maxLength={60}
                    className="pl-9"
                    onChange={(event) => setDestSearch(event.target.value)}
                    placeholder={destType === 'customer' ? 'Buscar placa ou cliente...' : 'Buscar pelo nome...'}
                  />
                </div>

                <div className="min-h-48 flex-1 overflow-y-auto rounded-md border bg-background">
                  {loadingDestinations ? (
                    <div className="flex h-full min-h-48 items-center justify-center text-sm text-muted-foreground">Carregando destinos...</div>
                  ) : destinations.length === 0 ? (
                    <div className="flex h-full min-h-48 flex-col items-center justify-center gap-2 px-6 text-center">
                      <DestinationIcon className="h-8 w-8 text-muted-foreground" />
                      <p className="text-sm font-medium">Nenhuma opção encontrada</p>
                      {destType === 'provider' && <p className="text-xs text-muted-foreground">Cadastre prestadores em Pessoas › Prestadores.</p>}
                    </div>
                  ) : destinations.map((destination) => {
                    const destinationSelected = dest?.id === destination.id;
                    return (
                      <Button
                        key={destination.id}
                        type="button"
                        variant="ghost"
                        onClick={() => setDest({ id: destination.id, label: destination.label, extra: destination.extra })}
                        aria-pressed={destinationSelected}
                        className={cn(
                          'h-auto w-full justify-start rounded-none border-b px-3 py-3 text-left last:border-0 hover:bg-muted/60',
                          destinationSelected && 'bg-primary/10 hover:bg-primary/15',
                        )}
                      >
                        <span className={cn(
                          'mr-3 flex h-9 w-9 shrink-0 items-center justify-center rounded-md border bg-muted/40',
                          destinationSelected && 'border-primary bg-primary text-primary-foreground',
                        )}>
                          {destinationSelected ? <Check className="h-4 w-4" /> : <DestinationIcon className="h-4 w-4 text-muted-foreground" />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-foreground">{destination.label}</span>
                          {destination.extra && <span className="block truncate text-xs font-normal text-muted-foreground">{destination.extra}</span>}
                        </span>
                        <span className={cn('ml-2 hidden text-xs font-medium sm:block', destinationSelected ? 'text-primary' : 'text-muted-foreground')}>
                          {destinationSelected ? 'Selecionado' : 'Selecionar'}
                        </span>
                      </Button>
                    );
                  })}
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between gap-3">
                    <Label htmlFor="stock-movement-notes">Observações</Label>
                    <span className="text-xs tabular-nums text-muted-foreground">{notes.length}/1000</span>
                  </div>
                  <Textarea id="stock-movement-notes" value={notes} maxLength={1000} rows={3} placeholder="Inclua uma observação para o histórico (opcional)" onChange={(event) => setNotes(event.target.value)} />
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="mx-auto flex min-h-full max-w-5xl flex-col gap-5 p-4 sm:p-6">
                <div>
                  <h3 className="text-base font-semibold">Revise a movimentação</h3>
                  <p className="text-sm text-muted-foreground">Confira os dados antes de confirmar a operação.</p>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <section className="rounded-md border bg-background p-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <PackageCheck className="h-4 w-4 text-primary" />
                        <h4 className="text-sm font-semibold">Itens selecionados</h4>
                      </div>
                      <Badge variant="secondary" className="tabular-nums">{selected.length}</Badge>
                    </div>
                    <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
                      {selected.map((item) => {
                        const ItemIcon = KIND_CONFIG[item.type].icon;
                        return (
                          <div key={`${item.type}-${item.id}`} className="flex items-start gap-2 border-b pb-2 last:border-0 last:pb-0">
                            <ItemIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                            <div className="min-w-0">
                              <p className="flex items-center gap-1.5 truncate text-xs font-semibold tabular-nums">{item.label} <StatusPill kind={item.type} value={item.status} /></p>
                              {item.type === 'kit' ? <KitComponents item={item} /> : item.extra && <p className="truncate text-[11px] text-muted-foreground">{item.extra}</p>}
                              <StockPlaceLabel place={item.place} compact />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>

                  <section className="rounded-md border bg-background p-4">
                    <div className="mb-3 flex items-center gap-2">
                      <DestinationIcon className="h-4 w-4 text-primary" />
                      <h4 className="text-sm font-semibold">Novo destino</h4>
                    </div>
                    <p className="text-sm font-semibold">{dest?.label}</p>
                    {dest?.extra && <p className="mt-1 text-xs text-muted-foreground">{dest.extra}</p>}
                    <Badge variant="outline" className="mt-3">
                      {STOCK_DESTINATION_TYPES.find((type) => type.value === destType)?.label}
                    </Badge>
                    {notes.trim() && (
                      <div className="mt-5 border-t pt-4">
                        <p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Observações</p>
                        <p className="whitespace-pre-wrap break-words text-sm">{notes.trim()}</p>
                      </div>
                    )}
                  </section>
                </div>

                <InlineNotice variant="info">
                  Cada item selecionado gerará uma movimentação individual no histórico, vinculada ao mesmo lote.
                </InlineNotice>

                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
                  <span>Itens e destino conferidos. A operação será processada de forma atômica.</span>
                </div>
              </div>
            )}
          </main>

          <MovementSummary selected={selected} destination={dest} destinationType={destType} notes={notes} onRemove={toggleItem} />
        </div>

        <DialogFooter className="shrink-0 flex-row items-center justify-between gap-2 border-t bg-background/80 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/70 sm:space-x-0 sm:px-6">
          <div>
            {step > 1 && (
              <Button type="button" variant="ghost" onClick={() => setStep((current) => current - 1)} disabled={move.isPending}>
                <ArrowLeft className="h-4 w-4 sm:mr-2" /> <span className="max-sm:sr-only">Voltar</span>
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={move.isPending}>Cancelar</Button>
            {step < 3 ? (
              <Button type="button" onClick={() => setStep((current) => current + 1)} disabled={!canAdvance} className="bg-gradient-to-r from-primary to-primary/85 shadow-md transition-all hover:shadow-lg">
                Continuar
                {selected.length > 0 && <span className="ml-1 max-sm:hidden">· {selected.length} {selected.length === 1 ? 'item' : 'itens'}</span>}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button type="button" onClick={submit} disabled={!dest || selected.length === 0 || move.isPending || isRevalidating} className="bg-gradient-to-r from-primary to-primary/85 shadow-md transition-all hover:shadow-lg">
                {(move.isPending || isRevalidating) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PackageCheck className="mr-2 h-4 w-4" />}
                {isRevalidating ? 'Atualizando itens...' : move.isPending ? 'Movimentando...' : (
                  <>
                    <span className="sm:hidden">Movimentar ({selected.length})</span>
                    <span className="max-sm:hidden">Movimentar {selected.length} {selected.length === 1 ? 'item' : 'itens'}</span>
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}